import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { Search, TrendingUp, Clock, ArrowRight, ChevronRight, Loader2, BarChart3, Target, Newspaper, GitCompareArrows, Layers, BookOpen, LineChart, Sparkles, GitBranch, Building2 } from 'lucide-react';
import { DashboardShell } from '@/components/DashboardShell';
import {
  DASHBOARD_CROSS_DAY_ANALOGUE_ENABLED,
  DASHBOARD_AI_RECEIPT_STACK_ENABLED,
  DASHBOARD_MAJOR_STORIES_ENABLED,
  DASHBOARD_MARKET_PREDICTION_ENABLED,
  DASHBOARD_SECTOR_ANALYSIS_ENABLED,
  DASHBOARD_STOCK_COMPARISON_ENABLED,
  DASHBOARD_TRADES_ENABLED,
  DASHBOARD_UPCOMING_EARNINGS_ENABLED,
  DASHBOARD_VALUE_CHAIN_ENABLED,
  DASHBOARD_QXO_ENABLED,
  STOCK_ATTRACTIVENESS_ENABLED,
} from '@/config/features';
import { MajorStoryCards } from '@/components/MajorStoryCards';
import { TradeStrategyCards } from '@/components/TradeStrategyCards';
import { getRecentStocks, addRecentStock, formatTimeAgo, RecentStock } from '@/utils/recentStocks';
import {
  earningsService,
  stockService,
  trendingService,
  marketPredictionService,
  sectorAnalysisService,
  majorStoriesService,
  tradeStrategiesService,
  marketResearchArticlesService,
  UpcomingEarning,
  TrendingStock,
  RecentAnalysis,
  MarketPredictionSummary,
  SectorAnalysisPayload,
  MajorStoriesPayload,
  TradeStrategiesPayload,
  MarketResearchArticleSummary,
} from '@/services/api';
import { humanizePredictionLabel } from '@/utils/marketPredictionCopy';

export function DashboardPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [recentStocks, setRecentStocks] = useState<RecentStock[]>([]);
  const [upcomingEarnings, setUpcomingEarnings] = useState<UpcomingEarning[]>([]);
  const [top5Earnings, setTop5Earnings] = useState<UpcomingEarning[]>([]);
  const [earningsLoading, setEarningsLoading] = useState(DASHBOARD_UPCOMING_EARNINGS_ENABLED);
  const [top5Loading, setTop5Loading] = useState(DASHBOARD_UPCOMING_EARNINGS_ENABLED);
  const [trendingStocks, setTrendingStocks] = useState<TrendingStock[]>([]);
  const [trendingLoading, setTrendingLoading] = useState(true);
  const [marketPrediction, setMarketPrediction] = useState<MarketPredictionSummary | null>(null);
  const [marketPredictionLoading, setMarketPredictionLoading] = useState(DASHBOARD_MARKET_PREDICTION_ENABLED);
  const [sectorAnalysis, setSectorAnalysis] = useState<SectorAnalysisPayload | null>(null);
  const [sectorAnalysisLoading, setSectorAnalysisLoading] = useState(DASHBOARD_SECTOR_ANALYSIS_ENABLED);
  const [majorStories, setMajorStories] = useState<MajorStoriesPayload | null>(null);
  const [majorStoriesLoading, setMajorStoriesLoading] = useState(DASHBOARD_MAJOR_STORIES_ENABLED);
  const [tradeStrategies, setTradeStrategies] = useState<TradeStrategiesPayload | null>(null);
  const [tradeStrategiesLoading, setTradeStrategiesLoading] = useState(DASHBOARD_TRADES_ENABLED);
  const [researchArticles, setResearchArticles] = useState<MarketResearchArticleSummary[]>([]);
  const [researchArticlesLoading, setResearchArticlesLoading] = useState(
    DASHBOARD_AI_RECEIPT_STACK_ENABLED,
  );
  const [_recentAnalyses, setRecentAnalyses] = useState<RecentAnalysis[]>([]);
  const [_analysesLoading, setAnalysesLoading] = useState(true);
  
  // Prefetch state to avoid duplicate requests
  const [prefetchedTickers, setPrefetchedTickers] = useState<Set<string>>(new Set());

  // Load recently visited stocks, upcoming earnings, and trending stocks on mount
  useEffect(() => {
    const stocks = getRecentStocks().slice(0, 3);
    setRecentStocks(stocks);
    
    // Fetch top 5 earnings by market cap for the card display
    const fetchTop5Earnings = async () => {
      setTop5Loading(true);
      try {
        const earnings = await earningsService.getUpcoming(5, 'market_cap');
        setTop5Earnings(earnings);
      } catch (error) {
        console.error('Failed to fetch top 5 earnings:', error);
      } finally {
        setTop5Loading(false);
      }
    };
    
    // Fetch upcoming earnings sorted by market cap (highest first)
    const fetchEarnings = async () => {
      setEarningsLoading(true);
      try {
        const earnings = await earningsService.getUpcoming(5, 'market_cap');
        setUpcomingEarnings(earnings);
      } catch (error) {
        console.error('Failed to fetch upcoming earnings:', error);
      } finally {
        setEarningsLoading(false);
      }
    };
    
    const fetchMarketPrediction = async () => {
      setMarketPredictionLoading(true);
      try {
        const response = await marketPredictionService.getLatest();
        setMarketPrediction(response?.summary ?? null);
      } catch (error) {
        console.error('Failed to fetch market prediction:', error);
      } finally {
        setMarketPredictionLoading(false);
      }
    };

    const fetchSectorAnalysis = async () => {
      setSectorAnalysisLoading(true);
      try {
        const response = await sectorAnalysisService.getLatest();
        setSectorAnalysis(response?.analysis ?? null);
      } catch (error) {
        console.error('Failed to fetch sector analysis:', error);
      } finally {
        setSectorAnalysisLoading(false);
      }
    };

    const fetchMajorStories = async () => {
      setMajorStoriesLoading(true);
      try {
        const response = await majorStoriesService.getLatest();
        setMajorStories(response);
      } catch (error) {
        console.error('Failed to fetch major stories:', error);
      } finally {
        setMajorStoriesLoading(false);
      }
    };

    const fetchTradeStrategies = async () => {
      setTradeStrategiesLoading(true);
      try {
        const response = await tradeStrategiesService.getLatest();
        setTradeStrategies(response);
      } catch (error) {
        console.error('Failed to fetch trade strategies:', error);
      } finally {
        setTradeStrategiesLoading(false);
      }
    };

    const fetchResearchArticles = async () => {
      setResearchArticlesLoading(true);
      try {
        const response = await marketResearchArticlesService.getLatest();
        setResearchArticles(response?.articles ?? []);
      } catch (error) {
        console.error('Failed to fetch research articles:', error);
      } finally {
        setResearchArticlesLoading(false);
      }
    };

    // Fetch trending stocks
    const fetchTrending = async () => {
      setTrendingLoading(true);
      try {
        const trending = await trendingService.getTrendingStocks();
        setTrendingStocks(trending);
      } catch (error) {
        console.error('Failed to fetch trending stocks:', error);
      } finally {
        setTrendingLoading(false);
      }
    };
    
    // Fetch recently analyzed stocks (from orchestrator runs)
    const fetchRecentAnalyses = async () => {
      setAnalysesLoading(true);
      try {
        const analyses = await stockService.getRecentAnalyses(5);
        setRecentAnalyses(analyses);
      } catch (error) {
        console.error('Failed to fetch recent analyses:', error);
      } finally {
        setAnalysesLoading(false);
      }
    };
    
    if (DASHBOARD_UPCOMING_EARNINGS_ENABLED) {
      fetchTop5Earnings();
      fetchEarnings();
    }
    if (DASHBOARD_MARKET_PREDICTION_ENABLED) {
      fetchMarketPrediction();
    }
    if (DASHBOARD_SECTOR_ANALYSIS_ENABLED) {
      fetchSectorAnalysis();
    }
    if (DASHBOARD_MAJOR_STORIES_ENABLED) {
      fetchMajorStories();
    }
    if (DASHBOARD_TRADES_ENABLED) {
      fetchTradeStrategies();
    }
    if (DASHBOARD_AI_RECEIPT_STACK_ENABLED) {
      fetchResearchArticles();
    }
    fetchTrending();
    fetchRecentAnalyses();
  }, []);

  const handleEarningClick = (earning: UpcomingEarning) => {
    // Store the company name for recent activity display
    addRecentStock(earning.ticker, earning.company);
    // Navigate to stock analysis page
    navigate(`/stock/${earning.ticker}`);
  };

  // Handle search submission
  const handleSearch = () => {
    const ticker = searchQuery.trim().toUpperCase();
    if (!ticker) return;
    
    // Validate ticker format (1-5 uppercase letters)
    if (!/^[A-Z]{1,5}$/.test(ticker)) {
      return;
    }
    
    // Add to recent stocks and navigate directly
    // StockAnalysisPage will handle fetching attractiveness data
    addRecentStock(ticker);
    setSearchQuery('');
    navigate(`/stock/${ticker}`);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  // PERFORMANCE: Prefetch stock data on hover for instant navigation
  const prefetchStockData = useCallback(async (ticker: string) => {
    // Don't prefetch if already fetched or currently prefetching
    if (prefetchedTickers.has(ticker)) return;
    
    // Mark as prefetching to avoid duplicates
    setPrefetchedTickers(prev => new Set(prev).add(ticker));
    
    try {
      // Fetch both endpoints in parallel and cache in sessionStorage
      const fetchPromises: Promise<unknown>[] = [stockService.getStockPrice(ticker)];
      if (STOCK_ATTRACTIVENESS_ENABLED) {
        fetchPromises.unshift(stockService.getAttractivenessScore(ticker));
      }

      const results = await Promise.all(fetchPromises);
      const scoreResult = STOCK_ATTRACTIVENESS_ENABLED
        ? (results[0] as Awaited<ReturnType<typeof stockService.getAttractivenessScore>>)
        : null;
      const priceResult = STOCK_ATTRACTIVENESS_ENABLED
        ? (results[1] as Awaited<ReturnType<typeof stockService.getStockPrice>>)
        : (results[0] as Awaited<ReturnType<typeof stockService.getStockPrice>>);
      
      // Store in sessionStorage for instant load when user clicks
      const cacheKey = `stock_${ticker}`;
      if (priceResult && (scoreResult || !STOCK_ATTRACTIVENESS_ENABLED)) {
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({
            scoreResult,
            priceResult,
            cachedAt: Date.now()
          }));
          console.log(`⚡ Prefetched ${ticker} - ready for instant navigation!`);
        } catch (e) {
          // Storage full, ignore
        }
      }
    } catch (error) {
      // Prefetch failed, user will just see normal load time
      console.debug(`Prefetch failed for ${ticker}:`, error);
    }
  }, [prefetchedTickers]);

  const formatPredictionPct = (value: number | null | undefined) => {
    if (value == null || Number.isNaN(value)) return '—';
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  const formatPredictionDate = (value: string | null | undefined) => {
    if (!value) return '—';
    try {
      const date = new Date(`${value}T12:00:00`);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return value;
    }
  };

  const marketPredictionIndexTags = (summary: MarketPredictionSummary) => {
    if (summary.index_forecasts?.length) {
      return summary.index_forecasts;
    }
    if (summary.predicted_spy_change_pct != null) {
      return [{ ticker: 'SPY', predicted_change_pct: summary.predicted_spy_change_pct }];
    }
    return [];
  };

  return (
    <DashboardShell activeTab="overview">
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-12"
        >
          <h1 className="mb-2 text-5xl md:text-6xl tracking-tight">
            Welcome back
          </h1>
          <p className="text-gray-600">
            Stock analysis and research platform
          </p>
        </motion.div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-12"
        >
          <div className="relative max-w-2xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value.toUpperCase())}
              onKeyDown={handleKeyPress}
              placeholder="Search by ticker symbol (e.g., AAPL, MSFT, GOOGL)..."
              className="w-full pl-12 pr-12 py-4 bg-white border border-gray-200 rounded-2xl focus:outline-none focus:border-gray-900 transition-colors"
              maxLength={5}
            />
            <button
              onClick={handleSearch}
              disabled={!searchQuery.trim()}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-900 transition-colors"
            >
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </motion.div>

        {DASHBOARD_QXO_ENABLED ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.21 }}
          className="mb-16"
        >
          <div className="mb-6 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-900">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-gray-900">QXO</h3>
              <p className="text-sm text-gray-500">Bottom-up investor desk · Beacon, Kodiak, TopBuild</p>
            </div>
          </div>

          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.26 }}
            type="button"
            onClick={() => navigate('/dashboard/qxo')}
            className="w-full cursor-pointer rounded-2xl border border-gray-200 bg-white p-6 transition-all duration-300 hover:border-gray-900 group"
          >
            <div className="flex items-center justify-between gap-6">
              <div className="min-w-0 text-left">
                <h4 className="text-gray-900 transition-transform group-hover:translate-x-1">
                  QXO, Inc. — execution, not concept
                </h4>
                <p className="mt-1 text-sm text-gray-500">
                  Q2 sales $3.25B · adj. EBITDA $272M · ~$18B PF revenue · 1.04B shares · same-store still the gap
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="rounded-lg bg-gray-100 px-3 py-1 text-sm text-gray-600">#2 roofing</span>
                  <span className="rounded-lg bg-gray-100 px-3 py-1 text-sm text-gray-600">#1 insulation</span>
                  <span className="rounded-lg bg-amber-50 px-3 py-1 text-sm text-amber-800">2030 $4B EBITDA plan</span>
                  <span className="rounded-lg bg-rose-50 px-3 py-1 text-sm text-rose-800">SSS −1.6%</span>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 shrink-0 text-gray-400 transition-all group-hover:translate-x-1 group-hover:text-gray-900" />
            </div>
          </motion.button>
        </motion.div>
        ) : null}

        {DASHBOARD_VALUE_CHAIN_ENABLED ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.22 }}
          className="mb-16"
        >
          <div className="mb-6 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600">
              <GitBranch className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-gray-900">Value Chain</h3>
              <p className="text-sm text-gray-500">Starlink kit hierarchy, suppliers, and BOM</p>
            </div>
          </div>

          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.28 }}
            type="button"
            onClick={() => navigate('/dashboard/value-chain')}
            className="w-full cursor-pointer rounded-2xl border border-gray-200 bg-white p-6 transition-all duration-300 hover:border-gray-900 group"
          >
            <div className="flex items-center justify-between gap-6">
              <div className="min-w-0 text-left">
                <h4 className="text-gray-900 transition-transform group-hover:translate-x-1">
                  Starlink Standard 4X
                </h4>
                <p className="mt-1 text-sm text-gray-500">
                  Browse the Starlink Standard 4X hierarchy, suppliers, and materials — open the full graph when you need it
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="rounded-lg bg-gray-100 px-3 py-1 text-sm text-gray-600">
                    14-order BOM
                  </span>
                  <span className="rounded-lg bg-blue-50 px-3 py-1 text-sm text-blue-800">
                    ST BiCMOS
                  </span>
                  <span className="rounded-lg bg-gray-100 px-3 py-1 text-sm text-gray-600">
                    Pulsar FEM
                  </span>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 shrink-0 text-gray-400 transition-all group-hover:translate-x-1 group-hover:text-gray-900" />
            </div>
          </motion.button>
        </motion.div>
        ) : null}

        {DASHBOARD_MARKET_PREDICTION_ENABLED ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="mb-16"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center">
                <Target className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-gray-900">Market Prediction</h3>
            </div>
          </div>

          <div className="space-y-3">
            {marketPredictionLoading ? (
              <div className="p-8 bg-white border border-gray-200 rounded-2xl flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-gray-400 animate-spin mr-3" />
                <span className="text-gray-500">Loading market prediction...</span>
              </div>
            ) : !marketPrediction ? (
              <div className="p-8 bg-white border border-gray-200 rounded-2xl text-center">
                <p className="text-gray-500">No market prediction available yet.</p>
                <p className="text-gray-400 text-sm mt-1">Predictions will appear after the next market analysis run.</p>
              </div>
            ) : (
              <motion.button
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.35 }}
                type="button"
                onClick={() => navigate('/market/prediction')}
                className="w-full p-6 bg-white border border-gray-200 rounded-2xl hover:border-gray-900 transition-all duration-300 cursor-pointer group"
              >
                <div className="flex items-center justify-between gap-6">
                  <div className="flex min-w-0 flex-wrap items-center gap-3 text-left">
                    <h4 className="shrink-0 text-gray-900 group-hover:translate-x-1 transition-transform">
                      {formatPredictionDate(marketPrediction.target_date ?? marketPrediction.reference_date)}
                    </h4>
                    {marketPredictionIndexTags(marketPrediction).map((row) => (
                      <span
                        key={row.ticker}
                        className="shrink-0 rounded-lg bg-gray-100 px-3 py-1 text-gray-600"
                      >
                        {row.ticker} {formatPredictionPct(row.predicted_change_pct)}
                      </span>
                    ))}
                    {marketPrediction.overall_direction ? (
                      <span className="shrink-0 rounded-lg bg-gray-100 px-3 py-1 text-gray-600 capitalize">
                        {marketPrediction.overall_direction}
                      </span>
                    ) : null}
                    {marketPrediction.regime || marketPrediction.narrative_scenario_tilt ? (
                      <span className="shrink-0 rounded-lg bg-gray-100 px-3 py-1 text-gray-600">
                        {humanizePredictionLabel(
                          marketPrediction.regime ?? marketPrediction.narrative_scenario_tilt,
                        )}
                      </span>
                    ) : null}
                    {marketPrediction.catalyst_event ? (
                      <span className="shrink-0 rounded-lg bg-amber-50 px-3 py-1 text-amber-800">
                        {marketPrediction.catalyst_event}
                        {marketPrediction.catalyst_surprise_bucket
                          ? ` · ${humanizePredictionLabel(marketPrediction.catalyst_surprise_bucket)}`
                          : ''}
                      </span>
                    ) : null}
                  </div>

                  <ChevronRight className="h-5 w-5 shrink-0 text-gray-400 transition-all group-hover:translate-x-1 group-hover:text-gray-900" />
                </div>
              </motion.button>
            )}
          </div>
        </motion.div>
        ) : null}

        {DASHBOARD_TRADES_ENABLED ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.26 }}
          className="mb-16"
        >
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-600">
                <LineChart className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-gray-900">Trades</h3>
                {tradeStrategies?.as_of_label ? (
                  <p className="text-sm text-gray-500">
                    {tradeStrategies.as_of_label}
                    {tradeStrategies.conviction_filter === 'high' ? ' · High conviction' : ''}
                  </p>
                ) : null}
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate('/market/trades')}
              className="text-sm text-gray-500 transition-colors hover:text-gray-900"
            >
              View all
            </button>
          </div>

          {tradeStrategiesLoading ? (
            <div className="flex items-center justify-center rounded-2xl border border-gray-200 bg-white p-8">
              <Loader2 className="mr-3 h-6 w-6 animate-spin text-gray-400" />
              <span className="text-gray-500">Loading trade strategies...</span>
            </div>
          ) : !tradeStrategies?.strategies?.length ? (
            <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center">
              <p className="text-gray-500">No trade strategies available yet.</p>
              <p className="mt-1 text-sm text-gray-400">
                Strategy panels will appear after the next trade ideas run.
              </p>
            </div>
          ) : (
            <TradeStrategyCards
              strategies={tradeStrategies.strategies}
              onStrategyClick={(strategy) =>
                navigate(`/market/trades/${tradeStrategies.reference_date}/${strategy.id}`)
              }
            />
          )}
        </motion.div>
        ) : null}

        {DASHBOARD_AI_RECEIPT_STACK_ENABLED ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.265 }}
          className="mb-16"
        >
          <div className="mb-6 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-600">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-gray-900">Research</h3>
              <p className="text-sm text-gray-500">Episode-backed allocation ideas</p>
            </div>
          </div>

          {researchArticlesLoading ? (
            <div className="flex items-center justify-center rounded-2xl border border-gray-200 bg-white p-8">
              <Loader2 className="mr-3 h-6 w-6 animate-spin text-gray-400" />
              <span className="text-gray-500">Loading research articles...</span>
            </div>
          ) : researchArticles.length === 0 ? (
            <div className="rounded-2xl border border-gray-200 bg-white p-8 text-sm text-gray-500">
              No research articles saved yet.
            </div>
          ) : (
            <div className="space-y-4">
              {researchArticles.map((article, index) => (
                <motion.button
                  key={article.article_id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.35 + index * 0.05 }}
                  type="button"
                  onClick={() =>
                    navigate(
                      `/market/research/${article.date}/${article.article_id}`,
                    )
                  }
                  className="w-full cursor-pointer rounded-2xl border border-gray-200 bg-white p-6 transition-all duration-300 hover:border-gray-900 group"
                >
                  <div className="flex items-center justify-between gap-6">
                    <div className="min-w-0 text-left">
                      <span className="mb-2 inline-flex items-center rounded-lg bg-violet-600 px-3 py-1 text-xs font-semibold tracking-wide text-white">
                        {article.released_label}
                      </span>
                      <h4 className="text-gray-900 transition-transform group-hover:translate-x-1">
                        {article.title}
                      </h4>
                      <p className="mt-1 text-sm text-gray-500">{article.audience}</p>
                      <p className="mt-2 text-sm leading-relaxed text-gray-600">
                        {article.summary}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {article.tags.map((tag) => (
                          <span
                            key={tag.label}
                            className={`rounded-lg px-3 py-1 text-sm ${
                              tag.tone === 'success'
                                ? 'bg-emerald-50 text-emerald-800'
                                : tag.tone === 'info'
                                  ? 'bg-violet-50 text-violet-800'
                                  : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {tag.label}
                          </span>
                        ))}
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 shrink-0 text-gray-400 transition-all group-hover:translate-x-1 group-hover:text-gray-900" />
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </motion.div>
        ) : null}

        {DASHBOARD_MAJOR_STORIES_ENABLED ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.27 }}
          className="mb-16"
        >
          <div className="mb-6 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500">
              <BookOpen className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-gray-900">Major Stories</h3>
              {majorStories?.metadata?.week_label ? (
                <p className="text-sm text-gray-500">{majorStories.metadata.week_label}</p>
              ) : null}
            </div>
          </div>

          {majorStoriesLoading ? (
            <div className="flex items-center justify-center rounded-2xl border border-gray-200 bg-white p-8">
              <Loader2 className="mr-3 h-6 w-6 animate-spin text-gray-400" />
              <span className="text-gray-500">Loading major stories...</span>
            </div>
          ) : !majorStories?.stories?.length ? (
            <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center">
              <p className="text-gray-500">No major stories available yet.</p>
            </div>
          ) : (
            <MajorStoryCards
              stories={majorStories.stories}
              onStoryClick={(story) => {
                if (story.rank != null) {
                  navigate(`/market/major-stories/${story.rank}`);
                }
              }}
            />
          )}
        </motion.div>
        ) : null}

        {/* Market Analysis */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.28 }}
          className="mb-16"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center">
                <Newspaper className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-gray-900">Market Analysis</h3>
            </div>
          </div>

          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.32 }}
            type="button"
            onClick={() => navigate('/market/analysis')}
            className="w-full p-6 bg-white border border-gray-200 rounded-2xl hover:border-gray-900 transition-all duration-300 cursor-pointer group"
          >
            <div className="flex items-center justify-between gap-6">
              <div className="min-w-0 text-left">
                <h4 className="text-gray-900 group-hover:translate-x-1 transition-transform">
                  Friday, June 26, 2026
                </h4>
                <p className="mt-1 text-sm text-gray-500">
                  Quick outline · Analog positioning · Risks to monitor
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="rounded-lg bg-gray-100 px-3 py-1 text-sm text-gray-600">S&P −0.27%</span>
                  <span className="rounded-lg bg-gray-100 px-3 py-1 text-sm text-gray-600">Defensive day</span>
                  <span className="rounded-lg bg-amber-50 px-3 py-1 text-sm text-amber-800">Hot PCE 4.1%</span>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 shrink-0 text-gray-400 transition-all group-hover:translate-x-1 group-hover:text-gray-900" />
            </div>
          </motion.button>
        </motion.div>

        {DASHBOARD_SECTOR_ANALYSIS_ENABLED ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.29 }}
          className="mb-16"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-violet-600 rounded-xl flex items-center justify-center">
                <Layers className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-gray-900">Sector Analysis</h3>
            </div>
          </div>

          <div className="space-y-3">
            {sectorAnalysisLoading ? (
              <div className="p-8 bg-white border border-gray-200 rounded-2xl flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-gray-400 animate-spin mr-3" />
                <span className="text-gray-500">Loading sector analysis...</span>
              </div>
            ) : !sectorAnalysis ? (
              <div className="p-8 bg-white border border-gray-200 rounded-2xl text-center">
                <p className="text-gray-500">No sector analysis available yet.</p>
                <p className="text-gray-400 text-sm mt-1">SOX sector analogs will appear after the next run.</p>
              </div>
            ) : (
              <motion.button
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.33 }}
                type="button"
                onClick={() => navigate('/market/sector-analysis')}
                className="w-full p-6 bg-white border border-gray-200 rounded-2xl hover:border-gray-900 transition-all duration-300 cursor-pointer group"
              >
                <div className="flex items-center justify-between gap-6">
                  <div className="min-w-0 text-left">
                    <h4 className="text-gray-900 group-hover:translate-x-1 transition-transform">
                      {sectorAnalysis.reference_date_label}
                    </h4>
                    <p className="mt-1 text-sm text-gray-500">
                      {sectorAnalysis.proxy_label} · Sector analogs · Catalyst narrative
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className={`rounded-lg px-3 py-1 text-sm ${
                        sectorAnalysis.percent_change >= 0
                          ? 'bg-emerald-50 text-emerald-800'
                          : 'bg-rose-50 text-rose-800'
                      }`}>
                        SOX {sectorAnalysis.percent_change > 0 ? '+' : ''}
                        {sectorAnalysis.percent_change.toFixed(2)}%
                      </span>
                      {sectorAnalysis.prior_day?.percent_change != null ? (
                        <span className="rounded-lg bg-gray-100 px-3 py-1 text-sm text-gray-600">
                          Prior {sectorAnalysis.prior_day.percent_change > 0 ? '+' : ''}
                          {sectorAnalysis.prior_day.percent_change.toFixed(2)}%
                        </span>
                      ) : null}
                      <span className="rounded-lg bg-violet-50 px-3 py-1 text-sm text-violet-800">
                        {sectorAnalysis.analog_count} analogs
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 shrink-0 text-gray-400 transition-all group-hover:translate-x-1 group-hover:text-gray-900" />
                </div>
              </motion.button>
            )}
          </div>
        </motion.div>
        ) : null}

        {DASHBOARD_CROSS_DAY_ANALOGUE_ENABLED ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-16"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center">
                <GitCompareArrows className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-gray-900">Cross Day Analogue Analysis</h3>
            </div>
          </div>

          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.34 }}
            type="button"
            onClick={() => navigate('/market/cross-day-analogue')}
            className="w-full p-6 bg-white border border-gray-200 rounded-2xl hover:border-gray-900 transition-all duration-300 cursor-pointer group"
          >
            <div className="flex items-center justify-between gap-6">
              <div className="min-w-0 text-left">
                <h4 className="text-gray-900 group-hover:translate-x-1 transition-transform">
                  June 30 – July 8, 2026
                </h4>
                <p className="mt-1 text-sm text-gray-500">
                  Primary narrative · Secondary threads · Window synthesis
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="rounded-lg bg-gray-100 px-3 py-1 text-sm text-gray-600">6 sessions</span>
                  <span className="rounded-lg bg-gray-100 px-3 py-1 text-sm text-gray-600">Single-engine chip market</span>
                  <span className="rounded-lg bg-amber-50 px-3 py-1 text-sm text-amber-800">CPI fork July 14</span>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 shrink-0 text-gray-400 transition-all group-hover:translate-x-1 group-hover:text-gray-900" />
            </div>
          </motion.button>
        </motion.div>
        ) : null}

        {/* Trending Stocks */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-16"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-gray-900">Watchlist</h3>
            </div>
          </div>

          <div className="space-y-3">
            {trendingLoading ? (
              <div className="p-8 bg-white border border-gray-200 rounded-2xl flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-gray-400 animate-spin mr-3" />
                <span className="text-gray-500">Loading trending stocks...</span>
              </div>
            ) : trendingStocks.length === 0 ? (
              <div className="p-8 bg-white border border-gray-200 rounded-2xl text-center">
                <p className="text-gray-500">No trending stocks available.</p>
                <p className="text-gray-400 text-sm mt-1">Trending data will be populated soon.</p>
              </div>
            ) : (
              trendingStocks.map((stock, index) => (
                <motion.button
                  key={stock.ticker}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 + index * 0.05 }}
                  onClick={() => navigate(`/stock/${stock.ticker}`)}
                  onMouseEnter={() => prefetchStockData(stock.ticker)}
                  className="w-full p-6 bg-white border border-gray-200 rounded-2xl hover:border-gray-900 transition-all duration-300 cursor-pointer group"
                >
                  <div className="flex items-center justify-between gap-6">
                    <div className="text-left flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h4 className="text-gray-900 group-hover:translate-x-1 transition-transform">
                          {stock.company || stock.ticker}
                        </h4>
                        <span className="px-3 py-1 bg-gray-100 rounded-lg text-gray-600">
                          {stock.ticker}
                        </span>
                      </div>
                    </div>

                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-900 group-hover:translate-x-1 transition-all shrink-0" />
                  </div>
                </motion.button>
              ))
            )}
          </div>
        </motion.div>

        {DASHBOARD_UPCOMING_EARNINGS_ENABLED ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mb-16"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-gray-900">Upcoming Earnings</h3>
            </div>
            <button 
              onClick={() => navigate('/earnings')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              View all
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Top 5 Earnings Cards - Hidden for now */}
          {false && top5Loading ? (
            <div className="mb-8 p-8 bg-white border border-gray-200 rounded-2xl flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-gray-400 animate-spin mr-3" />
              <span className="text-gray-500">Loading top earnings...</span>
            </div>
          ) : false && top5Earnings.length > 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55 }}
              className="mb-8"
            >
              <h4 className="text-gray-900 font-semibold mb-4">Top 5 Upcoming Earnings by Market Cap</h4>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                {top5Earnings.map((earning, index) => {
                  // Format date to "Feb 10" format
                  // Use formatted date from API if available, otherwise format from rawDate
                  const getDisplayDate = () => {
                    // If date is "Today" or "Tomorrow", format from rawDate
                    if (earning.date === 'Today' || earning.date === 'Tomorrow') {
                      if (earning.rawDate) {
                        try {
                          const date = new Date(earning.rawDate);
                          const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                          return `${months[date.getMonth()]} ${date.getDate()}`;
                        } catch {
                          return earning.date;
                        }
                      }
                    }
                    // Otherwise use the formatted date from API (already in "Feb 10" format)
                    return earning.date;
                  };

                  // Format market cap (e.g., "$313B")
                  const formatMarketCap = (rawMarketCap: number) => {
                    if (rawMarketCap >= 1e12) {
                      return `$${(rawMarketCap / 1e12).toFixed(0)}T`;
                    } else if (rawMarketCap >= 1e9) {
                      return `$${(rawMarketCap / 1e9).toFixed(0)}B`;
                    } else if (rawMarketCap >= 1e6) {
                      return `$${(rawMarketCap / 1e6).toFixed(0)}M`;
                    }
                    return `$${rawMarketCap.toFixed(0)}`;
                  };

                  // Format EPS estimate
                  const formatEPS = (eps: number | null) => {
                    if (eps === null || eps === undefined) return 'N/A';
                    return `$${eps.toFixed(2)}`;
                  };

                  // Truncate company name
                  const truncateCompanyName = (name: string, maxLength: number = 20) => {
                    if (name.length <= maxLength) return name;
                    return name.substring(0, maxLength) + '...';
                  };

                  return (
                    <motion.button
                      key={earning.id || `${earning.ticker}-${index}`}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4, delay: 0.6 + index * 0.05 }}
                      onClick={() => handleEarningClick(earning)}
                      onMouseEnter={() => prefetchStockData(earning.ticker)}
                      className="bg-white border border-gray-200 rounded-xl p-4 hover:border-gray-900 transition-all duration-300 cursor-pointer group text-left"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <span className="px-2.5 py-1 bg-gray-100 rounded-lg text-gray-700 text-sm font-medium">
                          {earning.ticker}
                        </span>
                        <span className="text-gray-400 text-xs">#{index + 1}</span>
                      </div>
                      
                      <h5 className="text-gray-900 font-medium mb-2 text-sm leading-tight">
                        {truncateCompanyName(earning.company)}
                      </h5>
                      
                      <p className="text-gray-600 text-sm mb-4">
                        {getDisplayDate()}
                      </p>
                      
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500 text-xs">Market Cap</span>
                          <span className="text-gray-900 font-semibold text-sm">
                            {formatMarketCap(earning.rawMarketCap)}
                          </span>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500 text-xs">EPS Est.</span>
                          <span className="text-gray-900 font-semibold text-sm">
                            {formatEPS(earning.epsEstimated)}
                          </span>
                        </div>
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          ) : null}

          <div className="space-y-3">
            {earningsLoading ? (
              <div className="p-8 bg-white border border-gray-200 rounded-2xl flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-gray-400 animate-spin mr-3" />
                <span className="text-gray-500">Loading upcoming earnings...</span>
              </div>
            ) : upcomingEarnings.length === 0 ? (
              <div className="p-8 bg-white border border-gray-200 rounded-2xl text-center">
                <p className="text-gray-500">No upcoming earnings found.</p>
                <p className="text-gray-400 text-sm mt-1">Check back later for upcoming earnings reports.</p>
              </div>
            ) : (
              upcomingEarnings.map((earning, index) => (
                <motion.button
                  key={earning.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 + index * 0.05 }}
                  onClick={() => handleEarningClick(earning)}
                  onMouseEnter={() => prefetchStockData(earning.ticker)}
                  className="w-full p-6 bg-white border border-gray-200 rounded-2xl hover:border-gray-900 transition-all duration-300 cursor-pointer group"
                >
                  <div className="flex items-center justify-between gap-6">
                    <div className="text-left flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h4 className="text-gray-900 group-hover:translate-x-1 transition-transform">
                          {earning.company || earning.ticker}
                        </h4>
                        <span className="px-3 py-1 bg-gray-100 rounded-lg text-gray-600">
                          {earning.ticker}
                        </span>
                      </div>
                    </div>

                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-900 group-hover:translate-x-1 transition-all shrink-0" />
                  </div>
                </motion.button>
              ))
            )}
          </div>
        </motion.div>
        ) : null}

        {DASHBOARD_STOCK_COMPARISON_ENABLED ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="mb-16"
        >
          <motion.button
            onClick={() => navigate('/compare')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full p-6 bg-white border border-gray-200 rounded-2xl hover:border-gray-900 transition-all cursor-pointer text-left group"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center group-hover:bg-gray-800 transition-colors">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Stock Comparison</h3>
                <p className="text-sm text-gray-600">Compare up to 10 stocks side-by-side</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-900 ml-auto transition-colors" />
            </div>
          </motion.button>
        </motion.div>
        ) : null}

        {/* Recent Activity - Recently Visited Stocks */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.9 }}
          className="mb-32"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-gray-900">Recent Activity</h3>
          </div>

          {recentStocks.length === 0 ? (
            <div className="p-8 bg-white border border-gray-200 rounded-2xl text-center">
              <p className="text-gray-500">No recently viewed stocks yet.</p>
              <p className="text-gray-400 text-sm mt-1">Search for a ticker above to get started.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentStocks.map((stock, index) => (
                <motion.button
                  key={stock.ticker}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 1.0 + index * 0.1 }}
                  onClick={() => navigate(`/stock/${stock.ticker}`)}
                  onMouseEnter={() => prefetchStockData(stock.ticker)}
                  className="w-full p-6 bg-white border border-gray-200 rounded-2xl hover:border-gray-900 transition-all duration-300 cursor-pointer group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-3 mb-1">
                        <h4 className="text-gray-900 group-hover:translate-x-1 transition-transform">
                          {stock.companyName || stock.ticker}
                        </h4>
                        <span className="px-3 py-1 bg-gray-100 rounded-lg text-gray-600 text-sm">
                          {stock.ticker}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-500">
                        <Clock className="w-4 h-4" />
                        <span>{formatTimeAgo(stock.visitedAt)}</span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-900 group-hover:translate-x-1 transition-all" />
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </motion.div>
      </main>
    </DashboardShell>
  );
}

